import type { VueQueryPluginOptions } from '@tanstack/vue-query';
import { VueQueryPlugin, QueryClient, hydrate, dehydrate } from '@tanstack/vue-query';
// Nuxt 3 app aliases
import { useState } from '#app';
import { useSessionStore } from '~/composables/stores/session';
import { useApplicantIntegrationStore } from '~/composables/stores/ApplicantIntegration';
export default defineNuxtPlugin((nuxtApp) => {
  // Modify your Vue Query global settings here
  const useSession = useSessionStore();
  // Resolve the store here (plugin setup) so the correct Pinia instance is captured.
  // Calling useApplicantIntegrationStore() inside onError (which runs from a later DOM
  // event, outside any setup context) can resolve a different store instance, so the
  // mutation runs but the component reading it via storeToRefs never updates.
  const integrationStore = useApplicantIntegrationStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ingore because we don't need to type errors
  const onError = async (error: any) => {
    const { response, request } = error ?? {};
    // TEMP DIAGNOSTIC — identify which request/op triggered this toast. Useful for the
    // prod-only, no-response case (CORS / blocked / malformed URL / thrown error) where
    // `response` is undefined and the default "contact support" message shows. The
    // `failedUrl` pinpoints which call failed (e.g. /service-areas/{id}/program-forms,
    // /funds/funds-assistance-types, /service-areas/locations/{id}). Remove once found.
    // eslint-disable-next-line no-console
    console.error('[vue-query onError]', {
      message: error?.message,
      failedUrl: String(request ?? response?.url ?? ''),
      status: response?.status ?? error?.statusCode,
      data: response?._data ?? error?.data,
      stack: error?.stack,
    });
    let errorMessage = `Please try again or contact support.`;
    let apiErrorMessage = '';

    // update apiErrorMessage from response?._data.error.message or response?._data.error.Message
    if (response?._data?.error?.message) {
      apiErrorMessage = response._data.error.message;
    } else if (response?._data?.error?.Message) {
      apiErrorMessage = response._data.error.Message;
    }

    if (response?.status === 401) {
      useSession.timeout();
    } else if (response?.status >= 400 && apiErrorMessage) {
      errorMessage = apiErrorMessage;
    } else if (apiErrorMessage) {
      errorMessage = apiErrorMessage;
    }

    if (errorMessage === 'The dto field is required.')
      errorMessage =
        'Please ensure all required fields are completed and retry your request. If the problem persists, please contact your local administrator.';

    if (errorMessage.includes('File upload error:')) {
      errorMessage = 'There is an issue with the file you are trying to upload. Please confirm your file is valid and retry your upload.';
    }

    // For Community Services / Wellsky integration requests, dismissing the error toast
    // should also close the open match dialog flow (MatchDialog and its children).
    const requestUrl = String(request ?? response?.url ?? '');
    const isIntegrationError = requestUrl.includes('/integration');

    toastError(
      'Error',
      errorMessage,
      5000,
      isIntegrationError ? () => integrationStore.closeMatchDialogs() : undefined
    );
  };

  const onSuccess = async () => {
    useSession.resetTimer();
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      mutations: {
        onError,
        onSuccess,
      },
      queries: {
        staleTime: 5000,
        refetchOnWindowFocus: false,
        // retryDelay: (attemptIndex) => Math.min(3000 * 2 ** attemptIndex, 30000),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ingore because we don't need to type errors
        onError,
        onSuccess,
      },
    },
  });
  const options: VueQueryPluginOptions = { queryClient };

  nuxtApp.vueApp.use(VueQueryPlugin, options);
  const vueQueryClient = useState('@tanstack/vue-query');

  if (process.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      vueQueryClient.value = dehydrate(queryClient);
    });
  }

  if (process.client) {
    nuxtApp.hooks.hook('app:created', () => {
      hydrate(queryClient, vueQueryClient.value);
    });
  }
});
